/**
 * Activity Dialog Component
 *
 * Dialog for creating activities like Berufsschule, Praktikum, etc.
 * Unterstuetzt seit 2026-05-11 die gleiche Wiederholungs-Logik wie der
 * Einsatz-Dialog (taeglich / Mo-Fr / woechentlich + Serien-Vorschau).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2, Repeat, CheckCircle2, AlertTriangle, Sun, CalendarDays } from 'lucide-react';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Switch } from '../../components/ui/switch';
import { activitiesAPI } from '../../lib/api';
import { ACTIVITY_TYPES } from './index';
import { MultiDayPicker } from '../../components/MultiDayPicker';

const formatDateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
};

const ActivityDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  massnahmen,
  saving,
  onSave,
  recurrence = { mode: 'once', end_date: '' },
  setRecurrence,
  // Mehrtages-Modus
  multiDayDays = [],
  setMultiDayDays,
  // Wenn gesetzt: Bearbeitungs-Modus (kein Multi-Day, keine Serie)
  selectedActivity = null,
}) => {
  const isEditing = !!selectedActivity;
  const multiDayEnabled = !isEditing && multiDayDays.length > 0;
  // --- Serien-Vorschau (analog zu ShiftDialog) ---
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimer = useRef(null);

  const isSeries = recurrence?.mode && recurrence.mode !== 'once';
  const seriesReady = Boolean(
    isSeries &&
    formData.massnahme_id &&
    formData.date &&
    recurrence?.end_date
  );

  useEffect(() => {
    if (!seriesReady) {
      setPreview(null);
      return undefined;
    }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await activitiesAPI.previewSeries({
          massnahme_id: formData.massnahme_id,
          location_id: formData.location_id || '',
          activity_type: formData.activity_type,
          custom_label: formData.custom_label || null,
          start_date: formData.date,
          end_date: recurrence.end_date,
          pattern: recurrence.mode,
          skip_holidays: recurrence.skip_holidays !== false,
        });
        setPreview(res.data);
      } catch {
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [
    seriesReady,
    formData.massnahme_id,
    formData.location_id,
    formData.activity_type,
    formData.custom_label,
    formData.date,
    recurrence?.end_date,
    recurrence?.mode,
    recurrence?.skip_holidays,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[92vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Aktivität bearbeiten' : 'Neue Teilnehmer-Aktivität'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {!isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              Erstellen Sie eine Aktivität wie Berufsschule oder Praktikum, bei der keine Mitarbeiterzuweisung erforderlich ist.
            </div>
          )}

          <div className="space-y-2">
            <Label>Maßnahme *</Label>
            <Select
              value={formData.massnahme_id}
              onValueChange={(v) => setFormData({ ...formData, massnahme_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Maßnahme wählen..." />
              </SelectTrigger>
              <SelectContent>
                {massnahmen.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.color }} />
                      {m.short_code} - {m.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Datum *</Label>
            <DatePickerDE
              value={formData.date}
              onChange={(v) => setFormData({ ...formData, date: v })}
              placeholder="Datum"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm">Von <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                type="time"
                value={formData.start_time || ''}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                placeholder="HH:MM"
                data-testid="activity-start-time"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Bis <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                type="time"
                value={formData.end_time || ''}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                placeholder="HH:MM"
                data-testid="activity-end-time"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Art der Aktivität *</Label>
            <Select
              value={formData.activity_type}
              onValueChange={(v) => setFormData({ ...formData, activity_type: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Aktivität wählen..." />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.activity_type === 'sonstiges' && (
            <div className="space-y-2">
              <Label>Eigene Bezeichnung</Label>
              <Input
                value={formData.custom_label}
                onChange={(e) => setFormData({ ...formData, custom_label: e.target.value })}
                placeholder="z.B. Teammeeting, Weiterbildung..."
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Anzahl Teilnehmer (optional)</Label>
            <Input
              type="number"
              min="0"
              value={formData.participant_count}
              onChange={(e) => setFormData({ ...formData, participant_count: e.target.value })}
              placeholder="z.B. 12"
            />
          </div>

          <div className="space-y-2">
            <Label>Notizen</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optionale Anmerkungen..."
              rows={2}
            />
          </div>

          {/* Mehrtages-Auswahl — nur beim Anlegen, nicht beim Bearbeiten */}
          {setMultiDayDays && !isEditing && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <CalendarDays className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <Label className="text-sm font-medium">Mehrere Tage auswählen</Label>
                    <p className="text-xs text-muted-foreground">
                      Wähle gezielt einzelne Tage – Aktivitäten werden für alle ausgewählten Tage angelegt.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={multiDayEnabled}
                  onCheckedChange={(v) => {
                    if (v) {
                      setMultiDayDays(formData.date ? [formData.date] : []);
                    } else {
                      setMultiDayDays([]);
                    }
                  }}
                  data-testid="activity-multiday-toggle"
                />
              </div>
              {multiDayEnabled && (
                <MultiDayPicker
                  selectedDays={multiDayDays}
                  onChange={setMultiDayDays}
                  weekStart={formData.date ? new Date(formData.date + 'T00:00:00') : new Date()}
                />
              )}
            </div>
          )}

          {/* Wiederholung — analog zum Einsatz-Dialog, ausgeblendet bei Mehrtages-Modus oder Edit-Mode */}
          {setRecurrence && !multiDayEnabled && !isEditing && (
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-3 flex items-center gap-2">
                <Repeat className="h-4 w-4" />
                Wiederholung
              </Label>
              <RadioGroup
                value={recurrence.mode}
                onValueChange={(m) => setRecurrence({ ...recurrence, mode: m })}
                className="space-y-2"
              >
                {[
                  { v: 'once', label: 'Einmalig', desc: 'Nur am gewählten Datum' },
                  { v: 'daily', label: 'Täglich', desc: 'Jeden Tag bis Enddatum' },
                  { v: 'weekdays', label: 'Mo–Fr', desc: 'Werktage bis Enddatum' },
                  { v: 'weekly', label: 'Wöchentlich', desc: 'Jede Woche am gleichen Wochentag' },
                ].map(({ v, label, desc }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setRecurrence({ ...recurrence, mode: v })}
                    className="flex items-start gap-2 text-left w-full hover:bg-muted/40 rounded-md p-2 transition-colors"
                    data-testid={`activity-recurrence-${v}`}
                  >
                    <RadioGroupItem value={v} id={`act-rec-${v}`} className="mt-0.5 pointer-events-none" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{label}</div>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                  </button>
                ))}
              </RadioGroup>

              {recurrence.mode !== 'once' && (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs text-muted-foreground">Enddatum *</Label>
                  <DatePickerDE
                    value={recurrence.end_date}
                    onChange={(v) => setRecurrence({ ...recurrence, end_date: v })}
                    placeholder="Letzter Tag der Serie"
                  />
                  <div className="flex items-center justify-between gap-2 pt-1 pb-1 rounded-md bg-secondary/30 px-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Sun className="h-4 w-4 text-amber-500" />
                      <span>Schulferien überspringen</span>
                    </div>
                    <Switch
                      checked={recurrence.skip_holidays !== false}
                      onCheckedChange={(v) => setRecurrence({ ...recurrence, skip_holidays: v })}
                      data-testid="activity-skip-holidays-toggle"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tage mit bereits vorhandener Aktivität dieser Maßnahme werden übersprungen.
                    {recurrence.skip_holidays !== false && ' Ferientage des Standort-Bundeslandes ebenfalls.'}
                  </p>

                  {seriesReady && (
                    <div
                      className="mt-3 rounded-lg border border-border/60 bg-secondary/20 p-3 space-y-2"
                      data-testid="activity-series-preview"
                    >
                      {previewLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Vorschau wird berechnet …
                        </div>
                      ) : preview ? (
                        <>
                          <div className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="font-medium">
                                {preview.will_create} Aktivitäten werden erstellt
                                {preview.will_skip > 0 && (
                                  <span className="ml-1 text-muted-foreground font-normal">
                                    · {preview.will_skip} übersprungen
                                    {preview.will_skip_holiday ? ` (${preview.will_skip_holiday} Ferien)` : ''}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Zeitraum insgesamt: {preview.total_candidates} Tag(e)
                              </p>
                            </div>
                          </div>
                          {preview.items?.length > 0 && (
                            <div className="max-h-32 overflow-y-auto rounded border border-border/40 bg-background/60 divide-y divide-border/30">
                              {preview.items.slice(0, 50).map((it) => (
                                <div
                                  key={it.date}
                                  className={`flex items-center justify-between px-2 py-1 text-xs ${
                                    it.has_conflict
                                      ? 'text-amber-700 dark:text-amber-300'
                                      : it.is_holiday
                                      ? 'text-blue-700 dark:text-blue-300'
                                      : ''
                                  }`}
                                >
                                  <span className="font-mono">{formatDateLabel(it.date)}</span>
                                  {it.has_conflict ? (
                                    <span className="inline-flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      übersprungen{it.conflict_with ? ` – ${it.conflict_with}` : ''}
                                    </span>
                                  ) : it.is_holiday ? (
                                    <span className="inline-flex items-center gap-1">
                                      <Sun className="h-3 w-3" />
                                      Ferien{it.holiday_name ? ` – ${it.holiday_name}` : ''}
                                    </span>
                                  ) : (
                                    <span className="text-emerald-600 dark:text-emerald-400">wird erstellt</span>
                                  )}
                                </div>
                              ))}
                              {preview.items.length > 50 && (
                                <div className="px-2 py-1 text-[11px] text-muted-foreground italic text-center">
                                  … und {preview.items.length - 50} weitere
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">Abbrechen</Button>
            <Button
              onClick={onSave}
              disabled={saving || (multiDayEnabled && multiDayDays.length === 0)}
              data-testid="activity-save-btn"
              className="w-full sm:w-auto"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {multiDayEnabled
                ? `${multiDayDays.length} Aktivitäten erstellen`
                : (isEditing ? 'Speichern' : 'Erstellen')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDialog;
